import { useEffect, useState } from "react";
import EmailGate from "../components/EmailGate";
import { hasStoredContentAccess } from "../lib/gatedContentTracking";

const ONBOARDING_ACCESS_KEY = "asha_onboarding_guide_access";

export const STYLES = `
.guide-doc {
  --paper: #ffffff;
  --paper-2: #f6f9ff;
  --ink: #0f172a;
  --ink-2: #334155;
  --ink-3: #64748b;
  --ink-4: #94a3b8;
  --line: #e2e8f0;
  --line-2: #eef2f7;
  --line-3: #f1f5fb;
  --accent: #2563eb;
  --accent-deep: #1d4ed8;
  --accent-soft: #eff6ff;
  --accent-ink: #1e40af;
  --accent-border: #bfdbfe;

  font-family: 'Inter', system-ui, -apple-system, sans-serif;
  color: var(--ink);
  font-size: 15.5px;
  line-height: 1.7;
  -webkit-font-smoothing: antialiased;
  text-rendering: optimizeLegibility;
}

.guide-doc *,
.guide-doc *::before,
.guide-doc *::after { box-sizing: border-box; }

.guide-doc a { color: var(--accent); text-decoration: none; }
.guide-doc a:hover { text-decoration: underline; }

.guide-doc .page {
  max-width: 880px;
  margin: 56px auto;
  background: var(--paper);
  border: 1px solid var(--line);
  border-radius: 14px;
  box-shadow:
    0 1px 0 rgba(15,23,42,0.02),
    0 30px 60px -30px rgba(15,23,42,0.18),
    0 12px 24px -16px rgba(15,23,42,0.10);
  overflow: hidden;
}

/* TOP BAR */
.guide-doc .topbar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 22px 56px;
  border-bottom: 1px solid var(--line);
  background: var(--paper);
}
.guide-doc .brand {
  display: flex;
  align-items: center;
  gap: 12px;
  font-weight: 600;
  font-size: 15px;
  letter-spacing: -0.01em;
  color: var(--ink);
}
.guide-doc .brand-mark {
  width: 26px; height: 26px;
  border-radius: 6px;
  overflow: hidden;
  flex-shrink: 0;
}
.guide-doc .brand-mark img { width: 100%; height: 100%; display: block; object-fit: cover; }
.guide-doc .brand-meta { color: var(--ink-3); font-weight: 400; }
.guide-doc .pill {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 5px 12px;
  border-radius: 999px;
  background: var(--accent-soft);
  color: var(--accent-ink);
  font-size: 11px;
  font-weight: 600;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  border: 1px solid var(--accent-border);
}
.guide-doc .pill::before {
  content: "";
  width: 6px; height: 6px;
  border-radius: 99px;
  background: var(--accent);
  box-shadow: 0 0 0 3px rgba(37, 99, 235, 0.18);
}

/* HERO */
.guide-doc .hero {
  padding: 72px 56px 48px;
  border-bottom: 1px solid var(--line);
  position: relative;
  background:
    radial-gradient(900px 280px at 90% -80px, rgba(191, 219, 254, 0.5), transparent 60%),
    radial-gradient(700px 240px at 0% 0%, rgba(239, 246, 255, 0.7), transparent 60%),
    var(--paper);
}
.guide-doc .eyebrow {
  font-size: 11px;
  letter-spacing: 0.16em;
  text-transform: uppercase;
  color: var(--ink-3);
  margin-bottom: 22px;
  font-weight: 600;
}
.guide-doc .eyebrow b { color: var(--accent-ink); font-weight: 600; }

.guide-doc h1.title {
  font-weight: 700;
  font-size: 56px;
  line-height: 1.04;
  letter-spacing: -0.032em;
  margin: 0 0 18px;
  color: var(--ink);
  max-width: 26ch;
  text-wrap: balance;
}
.guide-doc h1.title em {
  color: var(--accent);
  font-style: normal;
}
.guide-doc .title-sub {
  font-size: 18px;
  color: var(--ink-2);
  margin: 0 0 32px;
  max-width: 42ch;
  font-weight: 400;
}
.guide-doc .lede {
  font-size: 17px;
  line-height: 1.65;
  color: var(--ink-2);
  max-width: 64ch;
  margin: 0 0 40px;
  text-wrap: pretty;
}

.guide-doc .meta-grid {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  border-top: 1px solid var(--line);
  border-bottom: 1px solid var(--line);
}
.guide-doc .meta-cell {
  padding: 18px 22px 18px 0;
  border-right: 1px solid var(--line-2);
}
.guide-doc .meta-cell:not(:first-child) { padding-left: 22px; }
.guide-doc .meta-cell:last-child { border-right: 0; padding-right: 0; }
.guide-doc .meta-label {
  font-size: 10.5px;
  letter-spacing: 0.12em;
  text-transform: uppercase;
  color: var(--ink-3);
  margin-bottom: 8px;
  font-weight: 600;
}
.guide-doc .meta-value {
  font-size: 17px;
  font-weight: 600;
  color: var(--ink);
  letter-spacing: -0.01em;
}
.guide-doc .meta-value small {
  display: block;
  font-size: 12.5px;
  color: var(--ink-3);
  font-weight: 400;
  margin-top: 4px;
  letter-spacing: 0;
}

/* SECTION */
.guide-doc section.s {
  padding: 56px 56px 60px;
  border-bottom: 1px solid var(--line);
}
.guide-doc section.s:last-of-type { border-bottom: 0; }
.guide-doc .s-eyebrow {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 18px;
}
.guide-doc .s-num {
  font-size: 11px;
  letter-spacing: 0.14em;
  text-transform: uppercase;
  color: var(--accent-ink);
  font-weight: 600;
  padding: 3px 9px;
  background: var(--accent-soft);
  border: 1px solid var(--accent-border);
  border-radius: 5px;
}
.guide-doc .s-rule {
  flex: 1;
  height: 1px;
  background: var(--line);
}
.guide-doc .s-title {
  font-size: 30px;
  font-weight: 600;
  letter-spacing: -0.02em;
  line-height: 1.18;
  color: var(--ink);
  margin: 0 0 14px;
  max-width: 28ch;
  text-wrap: balance;
}
.guide-doc .s-sub {
  font-size: 17px;
  color: var(--ink-2);
  margin: 0 0 28px;
  max-width: 64ch;
  line-height: 1.6;
}

.guide-doc p {
  font-size: 16px;
  line-height: 1.7;
  color: var(--ink-2);
  margin: 0 0 18px;
  max-width: 68ch;
  text-wrap: pretty;
}
.guide-doc p:last-child { margin-bottom: 0; }
.guide-doc strong { color: var(--ink); font-weight: 600; }
.guide-doc em.accent {
  color: var(--accent-ink);
  background: var(--accent-soft);
  font-style: normal;
  padding: 1px 6px;
  border-radius: 4px;
  font-weight: 500;
}

.guide-doc h3.sub {
  font-size: 18px;
  font-weight: 600;
  letter-spacing: -0.01em;
  color: var(--ink);
  margin: 32px 0 8px;
  display: flex;
  align-items: baseline;
  gap: 10px;
}
.guide-doc h3.sub::before {
  content: "";
  width: 6px;
  height: 6px;
  border-radius: 99px;
  background: var(--accent);
  display: inline-block;
  margin-right: 4px;
  flex-shrink: 0;
}
.guide-doc h3.sub small {
  font-size: 11px;
  font-weight: 600;
  letter-spacing: 0.1em;
  text-transform: uppercase;
  color: var(--accent-ink);
}

/* Simple lists - prose-style */
.guide-doc ul.simple {
  list-style: none;
  padding: 0;
  margin: 0 0 22px;
}
.guide-doc ul.simple li {
  padding: 4px 0 4px 22px;
  position: relative;
  font-size: 16px;
  line-height: 1.7;
  color: var(--ink-2);
  max-width: 68ch;
}
.guide-doc ul.simple li::before {
  content: "";
  position: absolute;
  left: 0;
  top: 14px;
  width: 6px; height: 6px;
  border-radius: 99px;
  background: var(--accent);
  opacity: 0.7;
}
.guide-doc ul.simple li b { color: var(--ink); font-weight: 600; }

/* Definition-style list (label — text on a line) */
.guide-doc dl.dl {
  margin: 0 0 22px;
  padding: 0;
  border-top: 1px solid var(--line-2);
}
.guide-doc dl.dl > div {
  display: grid;
  grid-template-columns: 200px 1fr;
  gap: 28px;
  padding: 14px 0;
  border-bottom: 1px solid var(--line-2);
  align-items: baseline;
}
.guide-doc dl.dl dt {
  font-size: 15px;
  font-weight: 600;
  color: var(--ink);
  letter-spacing: -0.005em;
}
.guide-doc dl.dl dt small {
  display: block;
  font-size: 11px;
  font-weight: 600;
  letter-spacing: 0.1em;
  text-transform: uppercase;
  color: var(--accent-ink);
  margin-top: 4px;
}
.guide-doc dl.dl dd {
  margin: 0;
  font-size: 15.5px;
  color: var(--ink-2);
  line-height: 1.65;
}

/* Pull quote */
.guide-doc blockquote.pull {
  margin: 28px 0;
  padding: 18px 0 18px 22px;
  border-left: 3px solid var(--accent);
  font-size: 18px;
  font-style: italic;
  color: var(--ink);
  line-height: 1.55;
  max-width: 60ch;
  font-weight: 400;
}

/* Summary table - very minimal, just rules */
.guide-doc table.summary {
  width: 100%;
  border-collapse: collapse;
  margin: 4px 0 0;
}
.guide-doc table.summary thead th {
  text-align: left;
  padding: 0 0 12px;
  font-size: 10.5px;
  letter-spacing: 0.12em;
  text-transform: uppercase;
  color: var(--ink-3);
  font-weight: 600;
  border-bottom: 1px solid var(--ink);
}
.guide-doc table.summary tbody td {
  padding: 16px 0;
  border-bottom: 1px solid var(--line-2);
  vertical-align: top;
}
.guide-doc table.summary tbody tr:last-child td { border-bottom: 0; }
.guide-doc table.summary .col-when { width: 32%; padding-right: 24px; }
.guide-doc table.summary .col-detail { width: 68%; }
.guide-doc table.summary .sm-when {
  font-size: 15px;
  font-weight: 600;
  color: var(--ink);
  letter-spacing: -0.005em;
}
.guide-doc table.summary .sm-detail {
  font-size: 15px;
  color: var(--ink-2);
  line-height: 1.65;
}

/* FAQ */
.guide-doc .faq-list {
  display: flex;
  flex-direction: column;
  gap: 24px;
  border-top: 1px solid var(--line-2);
  padding-top: 24px;
  margin-top: 4px;
}
.guide-doc .faq-item {
  padding-bottom: 24px;
  border-bottom: 1px dashed var(--line-2);
}
.guide-doc .faq-item:last-child {
  border-bottom: 0;
  padding-bottom: 0;
}
.guide-doc .faq-q {
  font-size: 18px;
  font-weight: 600;
  color: var(--ink);
  margin: 0 0 10px;
  letter-spacing: -0.01em;
  line-height: 1.4;
}
.guide-doc .faq-a {
  font-size: 16px;
  line-height: 1.7;
  color: var(--ink-2);
  margin: 0;
  max-width: 72ch;
}
.guide-doc .faq-a strong { color: var(--ink); font-weight: 600; }

/* Tail */
.guide-doc .tail {
  padding: 22px 56px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  background: var(--paper-2);
  font-size: 11px;
  letter-spacing: 0.1em;
  text-transform: uppercase;
  color: var(--ink-3);
  border-top: 1px solid var(--line);
  font-weight: 500;
}

/* Responsive */
@media (max-width: 820px) {
  .guide-doc .page { margin: 24px; }
  .guide-doc .hero,
  .guide-doc section.s,
  .guide-doc .topbar,
  .guide-doc .tail { padding-left: 28px; padding-right: 28px; }
  .guide-doc h1.title { font-size: 36px; max-width: none; }
  .guide-doc .s-title { font-size: 24px; }
  .guide-doc .meta-grid { grid-template-columns: 1fr 1fr; }
  .guide-doc .meta-cell { padding: 16px 0 !important; border-right: 0; border-bottom: 1px solid var(--line-2); }
  .guide-doc .meta-cell:nth-child(2n) { padding-left: 22px !important; border-left: 1px solid var(--line-2); }
  .guide-doc .meta-cell:nth-last-child(-n+2) { border-bottom: 0; }
  .guide-doc dl.dl > div { grid-template-columns: 1fr; gap: 4px; }
}

@media (max-width: 560px) {
  .guide-doc .page { margin: 14px; border-radius: 14px; }
  .guide-doc .hero,
  .guide-doc section.s,
  .guide-doc .topbar,
  .guide-doc .tail { padding-left: 22px; padding-right: 22px; }
  .guide-doc .topbar { padding-top: 14px; padding-bottom: 14px; }
  .guide-doc .hero { padding-top: 44px; padding-bottom: 36px; }
  .guide-doc section.s { padding-top: 40px; padding-bottom: 44px; }
  .guide-doc h1.title { font-size: 30px; }
  .guide-doc .lede { font-size: 15.5px; }
  .guide-doc .meta-grid { grid-template-columns: 1fr; }
  .guide-doc .meta-cell { padding-left: 0 !important; border-left: 0 !important; }
  .guide-doc .meta-cell:not(:last-child) { border-bottom: 1px solid var(--line-2); }
  .guide-doc table.summary,
  .guide-doc table.summary tbody,
  .guide-doc table.summary tr,
  .guide-doc table.summary td { display: block; width: auto !important; }
  .guide-doc table.summary thead { display: none; }
  .guide-doc table.summary tr {
    padding: 16px 0;
    border-bottom: 1px solid var(--line-2);
  }
  .guide-doc table.summary tbody tr:last-child { border-bottom: 0; }
  .guide-doc table.summary td { padding: 0 !important; border: 0 !important; }
  .guide-doc table.summary td + td { margin-top: 6px; }
  .guide-doc .tail {
    flex-direction: column;
    align-items: flex-start;
    gap: 6px;
    padding-top: 16px; padding-bottom: 16px;
  }
}

@media print {
  @page { size: letter; margin: 0.4in; }
  body, html { background: #fff !important; }
  body.guide-body { background: #fff !important; }
  .guide-doc { font-size: 11.5px !important; }
  .guide-doc .page {
    max-width: 100% !important;
    margin: 0 !important;
    border: none !important;
    border-radius: 0 !important;
    box-shadow: none !important;
  }
  .guide-doc .topbar { padding: 14px 24px; }
  .guide-doc .hero { padding: 32px 24px 24px; }
  .guide-doc h1.title { font-size: 28px; max-width: none; }
  .guide-doc .lede { font-size: 12.5px; }
  .guide-doc section.s { padding: 24px 24px 28px; }
  .guide-doc .s-title { font-size: 18px; }
  .guide-doc .tail { padding: 12px 24px; }
  .guide-doc h3.sub, .guide-doc .s-title, .guide-doc h1.title {
    break-after: avoid;
    page-break-after: avoid;
  }
  .guide-doc dl.dl > div, .guide-doc table.summary tr, .guide-doc blockquote.pull {
    break-inside: avoid;
    page-break-inside: avoid;
  }
  * { -webkit-print-color-adjust: exact !important; print-color-adjust: exact !important; }
}
`;

const summary = [
  {
    when: "Pre-Onboarding",
    detail:
      "Integration call (system utility installed) + comprehensive onboarding form (1 – 2 hours).",
  },
  {
    when: "Week 1",
    detail:
      "Configurations confirmed, data migration completed, PMS familiarization begins, LOAs submitted, ERA enrollments kicked off, equipment ordered, SOTA installation begins.",
  },
  {
    when: "Week 2",
    detail:
      "Phone/fax porting completes, payment terminals and phones live, front-office AI agents turn on, staff shift to fully using the Asha PMS.",
  },
  {
    when: "Week 3",
    detail:
      "Initial calibration of front-office AI agents; first revenue-cycle agents go live (others may still be waiting on ERA enrollments).",
  },
  {
    when: "Week 4",
    detail:
      "Remaining back-office AI agents go live — the AI workforce is in place across the front and back office.",
  },
  {
    when: "Weeks 5 – 12",
    detail:
      "Ongoing calibration with your Account Manager — adjustments continue until everything is effective and you see outcomes.",
  },
];

const OnboardingGuidePage = () => {
  const [isLocked, setIsLocked] = useState(true);

  useEffect(() => {
    if (
      hasStoredContentAccess(
        ONBOARDING_ACCESS_KEY,
        "asha_onboarding_guide_session_id"
      )
    ) {
      setIsLocked(false);
    }
  }, []);

  const handleAccessGranted = (email) => {
    if (typeof window !== "undefined") {
      window.localStorage.setItem(ONBOARDING_ACCESS_KEY, "true");
    }
    // eslint-disable-next-line no-console
    console.log("Onboarding guide access granted for:", email);
    setIsLocked(false);
  };

  useEffect(() => {
    const previousBg = document.body.style.background;
    const previousMargin = document.body.style.margin;
    document.body.style.background = "#eef3fb";
    document.body.style.margin = "0";
    document.body.classList.add("guide-body");
    document.title = "Asha — Client Onboarding Guide";

    return () => {
      document.body.style.background = previousBg;
      document.body.style.margin = previousMargin;
      document.body.classList.remove("guide-body");
    };
  }, []);

  return (
    <>
      {isLocked && (
        <EmailGate
          onAccessGranted={handleAccessGranted}
          title="Please enter your email to view the onboarding guide"
          description={
            <>
              Access to the{" "}
              <span className="text-blue-400 font-semibold">Asha Health</span>{" "}
              client onboarding guide is reserved for our leads and customers.
            </>
          }
          submitLabel="View guide"
          agreementNoun="this guide"
          confidentialityText="The information in this guide is intended for prospective and existing Asha Health customers. Sharing without prior written consent from Asha Health, Inc. is prohibited."
          sessionStorageKey="asha_onboarding_guide_session_id"
        />
      )}

      <div className="guide-doc">
        <style dangerouslySetInnerHTML={{ __html: STYLES }} />

        <div className="page">
        {/* TOP BAR */}
        <div className="topbar">
          <div className="brand">
            <div className="brand-mark">
              <img src="/assets/alogo.png" alt="Asha" />
            </div>
            <span>Asha Health</span>
            <span className="brand-meta">· asha.dental</span>
          </div>
          <span className="pill">Client Onboarding</span>
        </div>

        {/* HERO */}
        <div className="hero">
          <div className="eyebrow">
            <b>Onboarding Guide</b> &nbsp;·&nbsp; AI-Powered Dental Practice
            Management
          </div>
          <h1 className="title">
            Onboarding to the <em>Asha AI Operating System</em>
          </h1>
          <p className="title-sub">
            What to expect — week by week — during onboarding.
          </p>
          <p className="lede">
            This guide walks through every workstream we run during onboarding —
            AI agent activation, communications migration, RCM setup, and
            imaging install — and exactly what we'll need from you and your
            team along the way. Your dedicated Account Manager coordinates all
            of it so nothing falls through the cracks.
          </p>

          <div className="meta-grid">
            <div className="meta-cell">
              <div className="meta-label">Time to fully live</div>
              <div className="meta-value">
                4 weeks
                <small>from your onboarding start</small>
              </div>
            </div>
            <div className="meta-cell">
              <div className="meta-label">AM cadence</div>
              <div className="meta-value">
                2× / week
                <small>30 minutes per call, first 8 weeks</small>
              </div>
            </div>
            <div className="meta-cell">
              <div className="meta-label">AI calibration</div>
              <div className="meta-value">
                8 – 12 weeks
                <small>to fully optimize to your practice</small>
              </div>
            </div>
            <div className="meta-cell">
              <div className="meta-label">Prepared by</div>
              <div className="meta-value">
                Asha Health, Inc.
                <small>sales@asha.health</small>
              </div>
            </div>
          </div>
        </div>

        {/* § 01 — YOUR ROLE */}
        <section className="s">
          <div className="s-eyebrow">
            <span className="s-num">§ 01</span>
            <span className="s-rule" />
          </div>
          <h2 className="s-title">Your role in onboarding</h2>
          <p className="s-sub">
            There's not much to figure out on your own — the process is guided
            by our Account Managers from start to finish.
          </p>

          <p>
            Your onboarding involves multiple parallel workstreams — from AI
            agent activation and communications migration to RCM setup and
            imaging install. While that may sound like a lot, your{" "}
            <strong>dedicated Account Manager</strong> coordinates all of it,
            and our team handles the vast majority of the work behind the
            scenes.
          </p>

          <p>What we need from you and your team is straightforward:</p>

          <ul className="simple">
            <li>
              <b>Show up to your scheduled Account Management calls</b> — two
              30-minute calls per week.
            </li>
            <li>
              <b>Follow the action items</b> your Account Manager provides
              after each session.
            </li>
            <li>
              <b>Flag anything that doesn't look right</b> so we can adjust
              quickly.
            </li>
            <li>
              <b>Complete the onboarding form</b> before your start date —
              onboarding cannot begin without it.
            </li>
          </ul>

          <p>
            That's it. We handle the technical setup, system configuration,
            vendor coordination, and everything in between — so your team's
            focus stays on your patients. We take care of the rest.
          </p>
        </section>

        {/* § 02 — AT A GLANCE */}
        <section className="s">
          <div className="s-eyebrow">
            <span className="s-num">§ 02</span>
            <span className="s-rule" />
          </div>
          <h2 className="s-title">Onboarding at a glance</h2>
          <p className="s-sub">
            Five milestones mark your journey from systems-connected to fully
            calibrated. Multiple workstreams run in parallel between them, all
            coordinated by your Account Manager.
          </p>

          <dl className="dl">
            <div>
              <dt>
                Integration Call
                <small>Short call</small>
              </dt>
              <dd>
                A short call where we install a system utility on your server
                that helps us migrate your data and begin to validate it in
                advance.
              </dd>
            </div>
            <div>
              <dt>
                Onboarding Form
                <small>1 – 2 hours</small>
              </dt>
              <dd>
                Our comprehensive onboarding form takes about 1 – 2 hours to
                fill out end to end. Onboarding cannot begin until this is
                submitted.
              </dd>
            </div>
            <div>
              <dt>
                Onboarding Kickoff
                <small>A few days – weeks</small>
              </dt>
              <dd>
                Official kickoff, scheduled a few days to a few weeks after
                the integration call, depending on the soonest available slots
                on our implementation calendar.
              </dd>
            </div>
            <div>
              <dt>
                Fully Live
                <small>Week 4</small>
              </dt>
              <dd>
                Front office, communications, and revenue cycle management all
                running on Asha.
              </dd>
            </div>
            <div>
              <dt>
                Full Calibration
                <small>8 – 12 weeks</small>
              </dt>
              <dd>
                AI agents complete tuning to your practice's tone, workflows,
                and edge cases.
              </dd>
            </div>
          </dl>
        </section>

        {/* § 03 — TIMELINE */}
        <section className="s">
          <div className="s-eyebrow">
            <span className="s-num">§ 03</span>
            <span className="s-rule" />
          </div>
          <h2 className="s-title">The onboarding timeline</h2>
          <p className="s-sub">
            Here's exactly what happens at each stage of onboarding, and which
            workstreams are running in the background.
          </p>

          <h3 className="sub">
            Pre-Onboarding <small>Before kickoff</small>
          </h3>
          <p>
            Before the official start, we install a small system utility on
            your server that helps us migrate your data and begin validating
            it in advance. We also ask your team to complete our{" "}
            <strong>comprehensive onboarding form</strong> (about 1–2 hours
            end to end), which captures the workflows, preferences, and
            details we need to tailor the platform to your practice from day
            one.
          </p>
          <p>
            The <em className="accent">onboarding form</em> is the only hard
            blocker — onboarding cannot begin until it's submitted.
          </p>

          <h3 className="sub">
            Week 1 <small>Kicking off the high-latency workstreams</small>
          </h3>
          <p>
            Week 1 is about kicking off all the processes that have natural
            latency — ERA enrollments, LOAs for phone porting, equipment
            shipping, and imaging install — so they're complete by the time
            we need them. Several workstreams run in parallel:
          </p>
          <ul className="simple">
            <li>
              <b>Configurations confirmed.</b> We review and confirm every
              setting from your onboarding form.
            </li>
            <li>
              <b>Data migration completed.</b> We migrate your data from your
              existing PMS into the Asha PMS and give your team access to
              begin reviewing it, so you can give us the thumbs-up that the
              data looks correct.
            </li>
            <li>
              <b>PMS familiarization begins.</b> Your team starts getting
              hands-on with the Asha PMS.
            </li>
            <li>
              <b>LOAs submitted.</b> We submit the Letters of Authorization to
              port your phone and fax lines. These typically complete by the
              end of Week 2.
            </li>
            <li>
              <b>ERA enrollments kicked off.</b> ERA enrollments can take
              anywhere from 1 – 4 weeks to complete, so we start them now to
              be prepped when it's time to put your RCM live.
            </li>
            <li>
              <b>Equipment ordered.</b> Payment terminals and phones ship to
              your office.
            </li>
            <li>
              <b>SOTA Cloud installation begins.</b> We coordinate the install
              with the SOTA team; installation typically completes by the end
              of Week 2.
            </li>
          </ul>
          <p>
            <strong>PMS training sessions</strong> with your staff also begin
            in Week 1 and continue through Week 2.
          </p>

          <h3 className="sub">
            Week 2 <small>Comms go live & staff transition to the PMS</small>
          </h3>
          <p>
            Near the end of Week 2, we finish porting your VoIP and fax lines
            and set them up to work with the phones we shipped to you. We also
            set up your <strong>payment terminals</strong>, which should have
            arrived by then. Once porting is complete and your phones and fax
            are live, we <strong>turn on your front-office AI agents</strong>{" "}
            based on the settings we confirmed in Week 1.
          </p>
          <p>
            Toward the end of Week 2, we ask your staff to{" "}
            <em className="accent">shift to fully using the Asha PMS</em>.
            Weeks 1 and 2 were their time to become familiar with it; from
            here on, the PMS is your team's primary workspace.
          </p>

          <h3 className="sub">
            Week 3 <small>Front-office AI calibration & first revenue-cycle agents</small>
          </h3>
          <p>
            With your front-office AI agents now live, Week 3 is where we do
            the <strong>initial calibration and tuning</strong> of those
            agents — shaping their tone, scheduling rules, and call handling
            to match your practice.
          </p>
          <p>
            We also begin bringing some of your{" "}
            <strong>revenue-cycle AI agents</strong> live this week, though
            some may still be waiting on ERA enrollments to finish on the
            payer side before they can be activated.
          </p>

          <h3 className="sub">
            Week 4 <small>Back-office AI agents go live</small>
          </h3>
          <p>
            By Week 4, your staff is already using the Asha PMS and your
            front-office AI agents are already live. This is when we bring the
            rest of your <strong>back-office AI agents</strong> live —
            completing the AI workforce across both the front and back of your
            practice.
          </p>

          <h3 className="sub">
            Weeks 5 – 12 <small>Ongoing calibration</small>
          </h3>
          <p>
            Beyond Week 4, the focus shifts to{" "}
            <em className="accent">ongoing calibration</em> with your Account
            Manager. We meet regularly to make adjustments to your AI agents
            until everything is operating effectively and you start seeing
            outcomes.
          </p>
        </section>

        {/* § 04 — ACCOUNT MANAGEMENT */}
        <section className="s">
          <div className="s-eyebrow">
            <span className="s-num">§ 04</span>
            <span className="s-rule" />
          </div>
          <h2 className="s-title">Ongoing account management</h2>
          <p className="s-sub">
            Your dedicated Account Manager meets with you twice a week for the
            first 8 weeks. Every session is focused on tuning Asha to your
            practice's voice, workflows, and edge cases.
          </p>

          <p>
            The cadence is <strong>2 meetings per week, 30 minutes each</strong>
            , for the first 8 weeks from your onboarding start. AI calibration
            runs in parallel and typically completes within{" "}
            <strong>8–12 weeks</strong>.
          </p>

          <p>During these sessions we:</p>

          <ul className="simple">
            <li>
              <b>Adjust and fine-tune AI agents</b> to match your preferences
              and tone.
            </li>
            <li>
              <b>Audit flagged cases</b> together and resolve any issues that
              have surfaced.
            </li>
            <li>
              <b>Customize the system</b> to your practice's specific workflows.
            </li>
            <li>
              <b>Provide ongoing staff training</b> and answer questions as the
              team scales up on the platform.
            </li>
            <li>
              <b>Monitor system performance</b> across every workstream.
            </li>
          </ul>
        </section>

        {/* § 05 — CALIBRATION NOTE */}
        <section className="s">
          <div className="s-eyebrow">
            <span className="s-num">§ 05</span>
            <span className="s-rule" />
          </div>
          <h2 className="s-title">A note on AI calibration</h2>
          <p className="s-sub">
            Setting realistic expectations for what AI is — and isn't — out of
            the box.
          </p>

          <p>
            It's a bad expectation to come in and think that everything will
            be effective from day one. Unfortunately,{" "}
            <strong>AI is not a button that just does one thing</strong> —
            because it can do so many different things, you should expect to
            find issues that need adjustments throughout the{" "}
            <em className="accent">first 12 weeks</em>.
          </p>

          <p>
            AI handles thousands of possible variations in patient
            communication, scheduling, billing, and more. As your AI agents
            start handling real interactions, edge cases will naturally
            surface. When you encounter something that doesn't look right — a
            response that doesn't match your preferred tone, a scheduling rule
            that needs adjusting, a billing scenario that wasn't handled
            correctly — simply{" "}
            <em className="accent">flag it for your Account Manager</em>.
          </p>

          <p>
            During your meetings, we review every flagged case and adjust the
            system accordingly. Over the first 12 weeks, these iterative
            refinements allow Asha's AI to fully calibrate to the way your
            practice operates. The more feedback you provide, the faster the
            system optimizes.
          </p>

          <blockquote className="pull">
            Think of it like training a new team member: highly capable from
            day one, but learning your specific preferences over time through
            feedback and repetition.
          </blockquote>
        </section>

        {/* § 06 — SUMMARY */}
        <section className="s">
          <div className="s-eyebrow">
            <span className="s-num">§ 06</span>
            <span className="s-rule" />
          </div>
          <h2 className="s-title">Timeline summary</h2>
          <p className="s-sub">
            The full onboarding journey, at a glance.
          </p>

          <table className="summary">
            <thead>
              <tr>
                <th className="col-when">When</th>
                <th className="col-detail">What happens</th>
              </tr>
            </thead>
            <tbody>
              {summary.map((row) => (
                <tr key={row.when}>
                  <td className="col-when">
                    <div className="sm-when">{row.when}</div>
                  </td>
                  <td className="col-detail">
                    <div className="sm-detail">{row.detail}</div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </section>

        {/* § 07 — FAQ */}
        <section className="s">
          <div className="s-eyebrow">
            <span className="s-num">§ 07</span>
            <span className="s-rule" />
          </div>
          <h2 className="s-title">Frequently asked questions</h2>
          <p className="s-sub">
            Quick answers to the most common questions about onboarding.
          </p>

          <div className="faq-list">
            <div className="faq-item">
              <h3 className="faq-q">How much is the onboarding fee?</h3>
              <p className="faq-a">
                The onboarding fee is <strong>$3,000</strong>. If you've had
                the fee waived as part of your
                contract, the waiver is contingent on completing all
                pre-onboarding action items —{" "}
                <em className="accent">
                  before your onboarding kickoff call
                </em>
                . If those items aren't completed in time, or if your
                onboarding kickoff gets delayed as a result, we will charge
                the additional onboarding fee based on your plan.
              </p>
            </div>

            <div className="faq-item">
              <h3 className="faq-q">
                Will the AI be good to go at the end of the four-week
                onboarding period?
              </h3>
              <p className="faq-a">
                Your AI agents will be live by Week 4, but they{" "}
                <strong>require ongoing calibration up until Week 12</strong>.
                AI is not a button that just does one thing — because it can
                handle so many different things, you should expect to find
                issues that need adjustments throughout the first 12 weeks.
                Your Account Manager works with you during this period to make
                adjustments until everything is operating effectively across
                every scenario your practice encounters.
              </p>
            </div>
          </div>
        </section>

        {/* TAIL */}
        <div className="tail">
          <span>Asha Health, Inc.</span>
          <span>Confidential to recipient</span>
          <span>www.asha.health · sales@asha.health</span>
        </div>
        </div>
      </div>
    </>
  );
};

export default OnboardingGuidePage;
